import React from 'react';
import { TFunction } from 'i18next';
import _get from 'lodash/get';
import { FieldItem, Values } from '@lib/interfaces/form';
import Input from '@lib/components/Input/Input';
import { InputTypes } from '@lib/components/Input/enums';
import Select from '@lib/components/Select/Select';
import { getOptionValidation } from '@lib/utils/validationUtils';
import ChipOption from '@lib/components/Select/components/ChipOption';
import getChipTypesOptions from '@lib/components/Chip/utils/getChipTypesOptions';
import ChipColorValueContainer from '@lib/components/Select/components/ChipColorValueContainer';
import { ChipTypes } from '@lib/components/Chip/Chip';
import LegendFormErrors from '../LegendFormModal/LegendFormErrors';
import OptionsAddItemComponent from '../LegendFormModal/OptionsAddItemComponent';
import OptionsFieldOption from '../LegendFormModal/OptionsFieldOption';
import getLegendFormFieldValidations from './getLegendFormFieldValidations';
import { MonitoringType } from '../enums';

const chipTypes = [
  ChipTypes.chipAquamarine,
  ChipTypes.chipBlue,
  ChipTypes.chipCorral,
  ChipTypes.chipGreen,
  ChipTypes.chipLime,
  ChipTypes.chipOrange,
  ChipTypes.chipPink,
  ChipTypes.chipRed,
  ChipTypes.chipViolet,
  ChipTypes.chipYellow,
];

type Args = {
  monitoringType: MonitoringType;
  t: TFunction<'translation', undefined>;
};

// Helper function to check if a field is the last disabled 'to' field
const isLastToFieldDisabled = (values: Values, name?: string): boolean => {
  if (!name) return false;

  const match = name.match(/items\.(\d+)\.to/);
  if (!match) return false;

  const index = parseInt(match[1], 10);
  const itemsArray = Array.isArray(values.items) ? values.items : [];

  // Last row should be disabled only if there are more than 1 items
  return index === itemsArray.length - 1 && itemsArray.length > 1;
};

export default function getLegendFormFields({
  monitoringType,
  t,
}: Args): FieldItem[] {
  const colorOptions = getChipTypesOptions({
    t,
    chipTypes,
  });
  return [
    {
      name: 'items',
      label: t('select-options-list-template'),
      validation: getLegendFormFieldValidations({ t, monitoringType }),
      fieldComponents: {
        FieldArrayAddItemComponent: OptionsAddItemComponent,
        FieldArrayItemWrapperComponent: OptionsFieldOption,
      },
      fieldChangeCallBack: ({ setValue, name, values, trigger }) => {
        if (name === 'items') {
          const data = name ? _get(values, 'items', []) : [];

          if (Array.isArray(data) && data.length > 0) {
            let needsUpdate = false;
            const updatedData = [...data];

            // Set first item 'from' to 0
            if (updatedData[0] && updatedData[0].from !== '0') {
              updatedData[0] = { ...updatedData[0], from: '0' };
              needsUpdate = true;
            }

            // Fix chain of from/to values for all items
            for (let i = 0; i < updatedData.length; i += 1) {
              const currentItem = updatedData[i];

              if (i === 0) {
                // First item: from should be 0
                if (currentItem.from !== '0') {
                  updatedData[i] = { ...currentItem, from: '0' };
                  needsUpdate = true;
                }
              }
            }

            // Set last item 'to' based on monitoring type
            const lastIndex = updatedData.length - 1;
            if (updatedData[lastIndex]) {
              const expectedToValue =
                monitoringType === MonitoringType.Anomaly ? null : 100;
              if (updatedData[lastIndex].to !== expectedToValue) {
                updatedData[lastIndex] = {
                  ...updatedData[lastIndex],
                  to: expectedToValue,
                };
                needsUpdate = true;
              }
            }

            if (needsUpdate) {
              setValue('items', updatedData);
            }
          }
        }
        trigger();
      },
      subFieldsMaxLength: colorOptions.length,
      subFields: [
        {
          name: 'color',
          element: Select,
          validation: getOptionValidation({ t }),
          componentProps: {
            options: colorOptions,
            isClearable: false,
            isSearchable: false,
            defaultOptions: false,
            usePortal: true,
            components: {
              ValueContainer: ChipColorValueContainer,
              Option: ChipOption,
            },
            hasErrorAsTooltip: true,
            fullWidthMenu: false,
          },
          hideErrors: true,
        },
        {
          name: 'from',
          label: t('from'),
          element: Input,
          componentProps: {
            type: InputTypes.number,
            hasErrorAsTooltip: true,
            min: 0,
          },
          isDisabledFn: (values: Values, name?: string) => {
            // Extract array index from field name like "items.0.from", "items.1.from", etc.
            if (!name) return false;

            const match = name.match(/items\.(\d+)\.from/);
            if (!match) return false;

            const index = parseInt(match[1], 10);
            const itemsArray = Array.isArray(values.items) ? values.items : [];

            // First row (index 0) should be disabled only if there are more than 1 items
            return index === 0 && itemsArray.length > 1;
          },
          hideErrors: true,
        },
        {
          name: 'to',
          label: t('to'),
          element: Input,
          getComponentProps: (values: Values, name?: string) => {
            const baseProps = {
              type: InputTypes.number,
              hasErrorAsTooltip: true,
              min: 0,
            };

            if (monitoringType !== MonitoringType.Anomaly) {
              return baseProps;
            }

            const isLastAndDisabled = isLastToFieldDisabled(values, name);

            return {
              ...baseProps,
              ...(isLastAndDisabled && { value: '∞' }),
            };
          },
          isDisabledFn: isLastToFieldDisabled,
          hideErrors: true,
        },
      ],
    },
    {
      name: 'errors',
      customRender: () => <LegendFormErrors />,
    },
  ];
}
